import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { browserIdentity } from '../runtime.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

interface UserAgentDataLike {
  readonly brands: Array<{ brand: string; version: string }>;
  readonly mobile: boolean;
  readonly platform: string;
  getHighEntropyValues(hints: string[]): Promise<Record<string, unknown>>;
}

interface NavigatorWithUaData extends Navigator {
  readonly userAgentData?: UserAgentDataLike;
}

const identityHeaderNames = [
  'user-agent',
  'sec-ch-ua',
  'sec-ch-ua-full-version-list',
  'sec-ch-ua-platform',
  'sec-ch-ua-platform-version',
  'sec-ch-ua-arch',
  'sec-ch-ua-bitness',
  'sec-ch-ua-mobile',
] as const;

function serializeBrandList(value: unknown): string | null {
  if (!Array.isArray(value) || !value.every(isRecord)) {
    return null;
  }
  return value
    .map((entry) => {
      const brand = String(entry.brand ?? '')
        .replaceAll('\\', '\\\\')
        .replaceAll('"', '\\"');
      const version = String(entry.version ?? '')
        .replaceAll('\\', '\\\\')
        .replaceAll('"', '\\"');
      return `"${brand}";v="${version}"`;
    })
    .join(', ');
}

function quotedHeaderValue(value: unknown): string {
  return `"${String(value)}"`;
}

async function collect(): Promise<RawData> {
  const navigatorWithData = navigator as NavigatorWithUaData;
  const low = navigatorWithData.userAgentData;
  const high = low
    ? await low.getHighEntropyValues([
        'architecture',
        'bitness',
        'fullVersionList',
        'model',
        'platformVersion',
        'wow64',
      ])
    : {};
  let requestHeaders: Record<string, string> = {};
  try {
    const response = await fetch('/network.json', { cache: 'no-store' });
    const body: unknown = await response.json();
    if (isRecord(body) && isRecord(body.headers)) {
      for (const name of identityHeaderNames) {
        if (body.headers[name] != null) {
          requestHeaders[name] = String(body.headers[name]);
        }
      }
    }
  } catch {
    requestHeaders = {};
  }
  return {
    userAgent: navigator.userAgent,
    appVersion: navigator.appVersion,
    platform: navigator.platform,
    vendor: navigator.vendor,
    product: navigator.product,
    productSub: navigator.productSub,
    uaCh: {
      brands: low?.brands ?? [],
      fullVersionList: high.fullVersionList ?? [],
      platform: low?.platform ?? '',
      platformVersion: high.platformVersion ?? '',
      architecture: high.architecture ?? '',
      bitness: high.bitness ?? '',
      model: high.model ?? '',
      mobile: low?.mobile ?? false,
      wow64: high.wow64 ?? null,
    },
    requestHeaders,
  };
}

function validateBrowserIdentity(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  if (!/Windows NT 10\.0/.test(String(rawData.userAgent))) {
    reasons.push('User-Agent is not Windows 10');
  }
  if (
    !/Chrome\/\d+/.test(String(rawData.userAgent)) ||
    /Chromium|HeadlessChrome/.test(String(rawData.userAgent))
  ) {
    reasons.push('User-Agent is not branded desktop Google Chrome');
  }
  if (rawData.platform !== 'Win32') {
    reasons.push('navigator.platform must be Win32');
  }
  if (rawData.vendor !== 'Google Inc.') {
    reasons.push('navigator.vendor must be Google Inc.');
  }
  const uaCh = isRecord(rawData.uaCh) ? rawData.uaCh : {};
  const brands = Array.isArray(uaCh.brands) ? uaCh.brands.filter(isRecord) : [];
  if (!brands.some((brand) => brand.brand === 'Google Chrome')) {
    reasons.push('UA-CH does not include the Google Chrome brand');
  }
  if (uaCh.platform !== 'Windows' || uaCh.mobile !== false) {
    reasons.push('UA-CH is not Windows desktop');
  }
  const requestHeaders = isRecord(rawData.requestHeaders)
    ? rawData.requestHeaders
    : {};
  const expectedHeaders: Readonly<Record<string, string | null>> = {
    'user-agent': String(rawData.userAgent ?? ''),
    'sec-ch-ua': serializeBrandList(uaCh.brands),
    'sec-ch-ua-full-version-list': serializeBrandList(uaCh.fullVersionList),
    'sec-ch-ua-platform': quotedHeaderValue(uaCh.platform),
    'sec-ch-ua-platform-version': quotedHeaderValue(uaCh.platformVersion),
    'sec-ch-ua-arch': quotedHeaderValue(uaCh.architecture),
    'sec-ch-ua-bitness': quotedHeaderValue(uaCh.bitness),
    'sec-ch-ua-mobile': uaCh.mobile === false ? '?0' : '?1',
  };
  for (const name of identityHeaderNames) {
    const expected = expectedHeaders[name];
    const actual = requestHeaders[name];
    if (typeof actual != 'string') {
      reasons.push(`HTTP ${name} is missing`);
    } else if (expected == null || actual != expected) {
      reasons.push(`HTTP ${name} does not match the JavaScript identity`);
    }
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const identity = browserIdentity(rawData);
  const value = `${identity.name} ${identity.version}`.trim();
  if ([...value].length <= 32) {
    return value;
  }
  return `${identity.name} ${identity.version.split('.')[0]}`.trim();
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateBrowserIdentity(rawData);
}

export const browserIdentityTest: TestDefinition = {
  id: 'browserIdentity',
  title: 'Browser identity',
  category: 'shared',
  format,
  run,
  validate,
};
